'use client';

import { getCachedImage } from '@/lib/storage/image-cache';
import { getWsrvImageUrl } from '@/lib/ui/wsrv';
import { useEffect, useMemo, useState } from 'react';
import type { WorkspaceReferenceImage } from '../contracts/runtime-state';

type CachedPreviewSources = {
  key: string;
  byImageId: ReadonlyMap<string, string>;
};

function isLocalPreview(image: WorkspaceReferenceImage) {
  return Boolean(
    image.file ||
      image.previewUrl.startsWith('blob:') ||
      image.previewUrl.startsWith('data:')
  );
}

function getOriginalPreviewSource(image: WorkspaceReferenceImage) {
  return isLocalPreview(image)
    ? image.previewUrl
    : getWsrvImageUrl(image.previewUrl);
}

/**
 * Native contentEditable chips do not mount React's CachedWsrvImage.
 * Reuse the same image cache and wsrv URL policy as upload thumbnails, while
 * keeping the upload/input URLs and underlying reference records untouched.
 * Object URLs here belong to this hook and are revoked on cleanup.
 */
export function useWorkspaceImageMentionPreviews(
  images: WorkspaceReferenceImage[]
) {
  const sourcesKey = JSON.stringify(
    images.map((image) => [image.id, image.previewUrl, Boolean(image.file)])
  );
  const [cached, setCached] = useState<CachedPreviewSources>({
    key: '',
    byImageId: new Map(),
  });

  useEffect(() => {
    let cancelled = false;
    const ownedUrls: string[] = [];

    for (const image of images) {
      if (!image.previewUrl || isLocalPreview(image)) continue;

      void getCachedImage(image.previewUrl)
        .then((entry) => {
          if (cancelled || !entry?.blob) return;

          const objectUrl = URL.createObjectURL(entry.blob);
          ownedUrls.push(objectUrl);
          setCached((current) => {
            const next = new Map(
              current.key === sourcesKey ? current.byImageId : undefined
            );
            next.set(image.id, objectUrl);
            return { key: sourcesKey, byImageId: next };
          });
        })
        .catch(() => {
          // A cache miss or disabled IndexedDB must not block image previews.
        });
    }

    return () => {
      cancelled = true;
      for (const objectUrl of ownedUrls) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [sourcesKey]);

  return useMemo(
    () =>
      images.map((image) => ({
        ...image,
        previewUrl:
          (cached.key === sourcesKey
            ? cached.byImageId.get(image.id)
            : undefined) ?? getOriginalPreviewSource(image),
      })),
    [cached, images, sourcesKey]
  );
}
